import apiClient from "../lib/api-client";

export interface HSNCode {
  id: string;
  tenantId: string;
  hsnCode: string;
  hsnDescription: string;
  gstRate: number;
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  cessRate?: number;
  chapter?: string;
  heading?: string;
  subheading?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    products: number;
  };
}

export interface CreateHSNCodeDto {
  hsnCode: string;
  hsnDescription: string;
  gstRate: number;
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  cessRate?: number;
  chapter?: string;
  heading?: string;
  subheading?: string;
}

export interface UpdateHSNCodeDto extends Partial<CreateHSNCodeDto> {
  isActive?: boolean;
}

export interface HSNCodeListResponse {
  data: HSNCode[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

function mutationKey(storageKey: string): string {
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

export const hsnCodeService = {
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    gstRate?: number;
    isActive?: boolean;
  }): Promise<HSNCodeListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.search) searchParams.append("search", params.search);
    if (params?.gstRate)
      searchParams.append("gstRate", params.gstRate.toString());
    if (params?.isActive !== undefined)
      searchParams.append("isActive", params.isActive.toString());

    const response = await apiClient.get(`/hsn-codes?${searchParams}`);
    return response.data;
  },

  async getById(id: string): Promise<HSNCode> {
    const response = await apiClient.get(`/hsn-codes/${id}`);
    return response.data;
  },

  async create(data: CreateHSNCodeDto): Promise<HSNCode> {
    const storageKey = `medicorepay:hsn-code:create:${data.hsnCode}`;
    const response = await apiClient.post("/hsn-codes", data, {
      headers: { "Idempotency-Key": mutationKey(storageKey) },
    });
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  async update(id: string, data: UpdateHSNCodeDto): Promise<HSNCode> {
    const storageKey = `medicorepay:hsn-code:update:${id}`;
    const response = await apiClient.patch(`/hsn-codes/${id}`, data, {
      headers: { "Idempotency-Key": mutationKey(storageKey) },
    });
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    const storageKey = `medicorepay:hsn-code:delete:${id}`;
    await apiClient.delete(`/hsn-codes/${id}`, {
      headers: { "Idempotency-Key": mutationKey(storageKey) },
    });
    sessionStorage.removeItem(storageKey);
  },

  async seedDefaults(): Promise<{ created: number; message: string }> {
    const storageKey = "medicorepay:hsn-code:seed-defaults";
    const response = await apiClient.post(
      "/hsn-codes/seed-defaults",
      {},
      { headers: { "Idempotency-Key": mutationKey(storageKey) } },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },
};
